import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { doRequest } from '@r1-frontend/do-request';
import {
    requestByConnection,
    RequestDto,
    ResponseDto,
} from '@r1-frontend/api-domru/api-request/v1/user/request-by-connection';

import { btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { PolicyNote } from '@r1-frontend/ui-react/components/PolicyNote';
import { EResultPopupStatus } from '@r1-frontend/ui-react/components/popups/popupResult';
import { IndentContainer } from '@r1-frontend/ui-react/experimental/containers';
import { ACTIONS } from '@r1-frontend/shared/constants/analytics';
import { EProductsId } from '@r1-frontend/shared/types/EProductsId';

import PopupResult from '~/src/components/Popups/PopupResult';
import { POLICY_PAGE_LINK } from '~/src/constants';
import { USER_LOGIN_ROUTE } from '~/src/constants/routes';
import { dataLayerForCBPhonePush, dataLayerForCBPhonePushWrapper } from '~/src/helpers/eventsForDataLayer';
import { selectCsrf } from '~/src/store/selectors/auth/selectCsrf';

import * as ST from './styled';

export type TField = {
    value: string,
    isValid: boolean,
    clearInput: () => void,
}

export type TMessage = {
    ok: boolean,
    text: string,
}

export interface ISubmitParam {
    fio: string,
    phone: string,
}

interface IResultPopup {
    isOrderOpened: boolean,
    isResultOpened: boolean,
    resultTitle: string,
    resultText: string,
    resultType: EResultPopupStatus,
}

interface IProps {
    isLoading?: boolean,
    isPopup?: boolean,
    withAgreeLink?: boolean,
    option?: string,
    message?: TMessage | null,
    btnText?: string | React.ReactNode,
    submitRequest?: (param: ISubmitParam) => void,
    formFilledHandler?: () => void,
    title?: string,
    handleAuthClick?: () => void,
    onlyBecomeClient?: boolean,
    getParams?: React.ReactNode | string,
    onClose: () => void,
    isOpen: boolean,
    userLoginRoute?: string,
    authBtnText?: string,
}

const AuthForm: FC<IProps> = ({
    isLoading,
    title = 'Чтобы подключить услугу, станьте клиентом Дом.ру или авторизуйтесь',
    btnText = 'Подключиться',
    formFilledHandler,
    getParams,
    handleAuthClick,
    onlyBecomeClient = false,
    option,
    isPopup,
    isOpen,
    onClose,
    userLoginRoute,
    authBtnText = 'Войти',
}: IProps) => {
    const csrf = useSelector(selectCsrf);

    const [phone, setPhone] = useState<TField>();
    const [name, setName] = useState<TField>();
    const [isValidInputs, setIsValidInputs] = useState(false);
    const [isOpenPopup, setIsOpenPopup] = useState(true);
    const [startInput, setStartInput] = useState(false);

    useEffect(() => {
        setIsValidInputs(!!(phone?.isValid && name?.isValid));
        if (isValidInputs && formFilledHandler) {
            formFilledHandler();
        }
    }, [phone, name, formFilledHandler]);

    useEffect(() => {
        setStartInput(false);
    }, [option]);

    const onChangeHandler = (stateInput: TField, _setState: Dispatch<SetStateAction<TField | undefined>>) => {
        // @TODO stateInput.value убрать из условия после фикса nameDecorator (useEffect onchange) UI react
        if (!startInput && stateInput.value !== undefined) {
            dataLayerForCBPhonePush({ action: ACTIONS.started, requestId: null, option, isPopup });
            setStartInput(true);
        }
        _setState(stateInput);
    };

    const submitHandler = () => {
        if (isValidInputs) {
            submitRequest({
                fio: name?.value || '',
                phone: phone?.value || '',
                product_id: [EProductsId.internet],
                csrf,
            });
            phone?.clearInput();
            name?.clearInput();
            setPhone(undefined);
            setName(undefined);
        }
    };

    const [popup, setPopup] = useState<IResultPopup>({
        isOrderOpened: false,
        isResultOpened: false,
        resultTitle: '',
        resultText: '',
        resultType: EResultPopupStatus.DEFAULT,
    });

    const submitRequest = useCallback(async(params: RequestDto) => {
        const resp = await doRequest.apiRequest<ResponseDto>(requestByConnection(params));
        if (resp.isSuccess) {
            const { message, status } = resp.payload;
            dataLayerForCBPhonePushWrapper({
                status: resp.payload.status,
                option: resp.payload.status ? '' : resp.payload.message,
            });
            openPopupResult(!!status, message);
        } else {
            openPopupResult(false, 'Не удалось отправить заявку.');
        }
    }, []);

    const openPopupResult = useCallback((ok, message) => {
        setIsOpenPopup(false);
        setPopup({
            isOrderOpened: false,
            isResultOpened: true,
            resultType: ok ? EResultPopupStatus.SUCCESS : EResultPopupStatus.ERROR,
            resultTitle: ok ? 'Заявка на подключение отправлена' : 'Произошла ошибка',
            resultText: ok ? 'Мы свяжемся с вами в ближайшее время' : message,
        });
    }, []);

    const closePopup = useCallback(() => {
        onClose();
        setIsOpenPopup(true);
        setPopup(state => ({
            ...state,
            isResultOpened: false,
        }));
    }, []);

    return (
        <>
            {isOpenPopup &&
                <ST.Popup onClose={onClose} isOpen={isOpen}>
                    <ST.Wrapper>
                        <ST.Title>
                            {title}
                        </ST.Title>
                        <ST.Inputs>
                            <ST.Name
                                name='name'
                                onChange={(stateInput: TField) => onChangeHandler(stateInput, setName)}
                                placeholder='Как вас зовут?'
                                errorMessage='Ожидаются только буквы и дефисы'
                                data-test='field-name'
                                className={'ym-disable-keys'}
                            />
                            <ST.Phone
                                name='phone'
                                onChange={(stateInput: TField) => onChangeHandler(stateInput, setPhone)}
                                placeholder='Номер телефона'
                                errorMessage='Введите телефон в формате +7 (999) 999-99-99'
                                data-test='field-phone'
                                className={'ym-disable-keys'}
                            />
                        </ST.Inputs>
                        {!onlyBecomeClient && (
                            <IndentContainer $united={[0, 0, 12, 0]}>
                                <PolicyNote
                                    data-test='agreement'
                                    buttonText={btnText}
                                    agreeUrl={POLICY_PAGE_LINK}
                                />
                            </IndentContainer>
                        )}
                        <ST.ButtonBlock>
                            <ST.Button
                                type={btnTypes.ACCENT}
                                text={btnText}
                                disabled={!isValidInputs || isLoading}
                                onClick={submitHandler}
                            />
                            {onlyBecomeClient
                                ? <PolicyNote
                                    data-test='agreement'
                                    buttonText={btnText}
                                    agreeUrl={POLICY_PAGE_LINK}
                                />
                                : <ST.Button
                                    href={userLoginRoute ?? `${USER_LOGIN_ROUTE}${getParams}`}
                                    onClick={handleAuthClick}
                                    type={btnTypes.SECONDARY}
                                    text={authBtnText}
                                    data-test='submit-btn'
                                />
                            }
                        </ST.ButtonBlock>
                    </ST.Wrapper>
                </ST.Popup>
            }
            <PopupResult
                isOpen={popup.isResultOpened}
                onClose={closePopup}
                title={popup.resultTitle}
                text={popup.resultText}
                type={popup.resultType}
                data-test='popup-result'
            />
        </>
    );
};

export default AuthForm;
